"use client";

import { queryOptions, useQuery } from "@tanstack/react-query";
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy, version } from "pdfjs-dist";

import { isServer } from "@/src/lib/runtime";
import { queryClient } from "@/src/query/query-client";

// Initialize PDF.js worker
if (!isServer) {
  GlobalWorkerOptions.workerSrc = `/pdfjs/${version}/pdf.worker.min.mjs`;
}

export const PDF_QUERY_KEY = "pdf";

export function getPdfQueryKey(url: string) {
  return [PDF_QUERY_KEY, url] as const;
}

export async function fetchPdfDocument(url: string, signal?: AbortSignal) {
  signal?.throwIfAborted();

  const documentLoader = getDocument(url);
  const abortLoading = () => void documentLoader.destroy();

  signal?.addEventListener("abort", abortLoading, { once: true });

  try {
    return await documentLoader.promise;
  } catch (error) {
    if (!documentLoader.destroyed) {
      await documentLoader.destroy();
    }

    throw error;
  } finally {
    signal?.removeEventListener("abort", abortLoading);
  }
}

queryClient.getQueryCache().subscribe((event) => {
  if (event.type === "removed" && event.query.queryKey[0] === PDF_QUERY_KEY) {
    void (event.query.state.data as PDFDocumentProxy | undefined)?.destroy();
  }
});

export function getPdfPrefetchQueryOptions(url: string) {
  return queryOptions({
    queryKey: getPdfQueryKey(url),
    queryFn: ({ signal }) => fetchPdfDocument(url, signal),
    staleTime: Infinity,
    gcTime: 15 * 60 * 1000,
  });
}

export function usePdf(url: string) {
  return useQuery({
    queryKey: getPdfQueryKey(url),
    queryFn: ({ signal }) => fetchPdfDocument(url, signal),
    enabled: !!url,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}
